"use client";

import { MessageSquare } from "lucide-react";
import type { ReactElement, ReactNode } from "react";
import { cloneElement, isValidElement } from "react";
import type { FeedPost, FeedRequest, FeedResponse } from "@/actions/post";
import { Card, CardContent } from "@/components/ui/card";
import type { CommentsSheetViewer } from "./comments-sheet";
import { PostListClient } from "./post-list-client";

interface PostListProps {
  context: FeedRequest;
  viewer?: CommentsSheetViewer;
  className?: string;
  composer?: ReactNode;
  initialLimit?: number;
  emptyState?: React.ReactNode;
  initialPosts?: FeedPost[];
  initialFeed?: FeedResponse;
}

const DEFAULT_LIMIT = 10;

export function PostList({
  context,
  viewer,
  className,
  composer,
  initialLimit = DEFAULT_LIMIT,
  emptyState,
  initialPosts,
  initialFeed,
}: PostListProps) {
  const defaultEmptyState = (
    <Card className="border-border bg-surface">
      <CardContent className="flex flex-col items-center justify-center p-12 text-center">
        <div className="mb-4 rounded-full bg-surface-alt p-4">
          <MessageSquare className="h-8 w-8 text-text-secondary" />
        </div>
        <h3 className="mb-2 font-medium text-text-primary">No posts yet</h3>
        <p className="max-w-sm text-sm text-text-secondary">
          Be the first to share something with this community! Posts help
          members stay connected and engaged.
        </p>
      </CardContent>
    </Card>
  );

  const composerWithLimit =
    composer && isValidElement(composer)
      ? hasFeedLimitProp(composer)
        ? cloneElement(composer, {
            feedLimit: composer.props.feedLimit ?? initialLimit,
          })
        : composer
      : composer;

  return (
    <PostListClient
      context={context}
      initialLimit={initialLimit}
      viewer={viewer}
      composer={composerWithLimit}
      className={className}
      emptyState={emptyState ?? defaultEmptyState}
      initialPosts={initialPosts}
      initialFeed={initialFeed}
    />
  );
}

function hasFeedLimitProp(
  element: ReactElement,
): element is ReactElement<{ feedLimit?: number }> {
  const props = element.props as Record<string, unknown> | null | undefined;
  if (!props) {
    return false;
  }
  return "feedLimit" in props;
}
